/**
 * ENG-7253: map the catalog remote-MCP descriptor across the manager seam.
 *
 * The `/host/agent-integrations` response forwards the integration's
 * `integration_definitions.remote_mcp` column in **snake_case** (`remote_mcp`,
 * added by ENG-6993 / ADR-0033 Slice 2). Every host-side consumer, however,
 * reads the **camelCase** `remoteMcp` on `ResolvedIntegration`
 * (`buildRemoteMcpEntry(definition_id, integration.remoteMcp)` in the
 * claudecode adapter's `writeIntegrations` / `buildMcpJson`).
 *
 * Nothing bridged the two names, so the forwarded descriptor was dropped at the
 * manager and `integration.remoteMcp` was always `undefined`. For a remote-MCP
 * integration with an `INTEGRATION_REGISTRY` fallback (anchor-browser) the
 * adapter still rendered from the code registry, hiding the gap; but a
 * **pure-catalog** remote-MCP integration with no registry entry (monday.com)
 * silently never rendered its server into `.mcp.json` and none of its
 * `mcp__<id>__*` tools could bind.
 *
 * This helper performs the one mapping that closes the seam. It is intentionally
 * additive (spreads the original object) so all other consumers - connectivity
 * probes, the integration hash, the OAuth refresh loop - see an unchanged shape.
 */
import type { RemoteMcpSpec, HitlTier } from '@augmented/core';
import { HITL_TIER_ORDER } from '@augmented/core';

/**
 * ENG-8800 AC5 — an unrecognised `credential_delivery` must not downgrade in
 * silence.
 *
 * The normalisation below deliberately treats anything that is not the exact
 * string `'broker'` as env delivery, and that DIRECTION is correct: a value a
 * newer API adds must never be able to strip a running agent's `git`/`gh`
 * credentials. Fail-safe stays fail-safe. What was wrong is that it happened
 * without a word.
 *
 * The downgrade is worse than it sounds, because both host consumers key off
 * this one normalisation (`claudecode/index.ts` :6051 and :6156). On an
 * unrecognised value:
 *
 *   - `credentialDelivery` becomes `undefined`, so the
 *     `if (credentialDelivery === 'broker') continue` guard does not fire and
 *     the host tries to write `GITHUB_ACCESS_TOKEN` from `creds.access_token`
 *     — which the API deliberately stripped under the ENG-8344 flag, so
 *     nothing is written;
 *   - and `syncGitHubBrokerCredentialTooling(..., false)` REMOVES the git
 *     credential helper and the `gh` shim.
 *
 * Net: no env token and no broker helper. A total GitHub credential outage
 * produced by one wrong character in a server-side string, with nothing
 * logged anywhere. That is gordon's eleven silent days (ENG-8751) in
 * miniature, and it is the same silence that would have hidden a credential
 * cross-serve.
 *
 * The rule is deliberately narrow, because noise is HOW gordon hid:
 *
 *   absent (undefined/null) → an older API, or any ordinary non-github row.
 *                             Silent. This is most rows on the fleet and
 *                             logging it would drown the signal.
 *   'env' | 'broker'        → recognised. Silent.
 *   anything else, incl. '' → unrecognised. Log.
 *
 * The API only ever writes `'env'` or `'broker'`
 * (`host-runtime.ts:10686`), so on a healthy fleet this never fires — which
 * is the property that makes it worth alerting on when it does.
 */
const RECOGNISED_CREDENTIAL_DELIVERY = new Set(['env', 'broker']);

/**
 * Longest raw value we will echo. A payload field is not a length we control,
 * and a megabyte on one stderr line is its own kind of unreadable log.
 */
const MAX_SHOWN_VALUE_CHARS = 120;

export function describeUnrecognisedCredentialDelivery(
  value: unknown,
): string | null {
  // Absent is normal, not anomalous: rows predating ENG-8344 omit the field
  // entirely, as does every non-github integration.
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && RECOGNISED_CREDENTIAL_DELIVERY.has(value)) return null;
  // `JSON.stringify`, not `'${value}'`. This value arrives in an API payload,
  // so its content is not ours to trust: a `\n` in it would let it forge a
  // second stderr line, and an ESC could rewrite what an operator sees in a
  // terminal. Forging THIS line is a particularly attractive trick, because it
  // is the line that says a credential path silently degraded — an attacker who
  // can write the field could append a reassuring one. JSON.stringify escapes
  // every control character below 0x20 (so `\n`, `\r` and `\x1b` all become
  // literal text) and quotes the result, which the old single quotes only
  // pretended to do.
  const shown =
    typeof value === 'string'
      ? JSON.stringify(
          value.length > MAX_SHOWN_VALUE_CHARS
            ? `${value.slice(0, MAX_SHOWN_VALUE_CHARS)}…`
            : value,
        )
      : `<${typeof value}>`;
  return `unrecognised credential_delivery ${shown} — treating as env delivery. `
    + `If this row is a github_app under the github-broker-credentials flag, the API has `
    + `stripped its token AND the broker helper will be removed, leaving the agent with no `
    + `GitHub credential at all (ENG-8800 AC5)`;
}

/** An integration object as it arrives from `/host/agent-integrations`. */
export interface HostIntegrationWithRemoteMcp {
  remote_mcp?: RemoteMcpSpec | null;
  /** ENG-7358: catalog stdio-MCP cutover flag (metadata.stdio_mcp). */
  stdio_mcp?: boolean | null;
  /** ENG-8344: 'broker' when the payload deliberately carries no token. */
  credential_delivery?: string | null;
  /**
   * ENG-10614: per-tool HITL floors from `tool_definitions.min_hitl_tier`,
   * forwarded only for remote-MCP definitions that have rows.
   */
  tool_tiers?: Record<string, string> | null;
}

/**
 * Return the integration with `remoteMcp` populated from the forwarded
 * snake_case `remote_mcp` column (or `undefined` when absent), and `stdioMcp`
 * from the forwarded `stdio_mcp` flag (ENG-7358, same seam). The original
 * fields are preserved verbatim.
 */
export function withResolvedRemoteMcp<T extends HostIntegrationWithRemoteMcp>(
  integration: T,
): T & {
  remoteMcp: RemoteMcpSpec | undefined;
  stdioMcp: boolean | undefined;
  credentialDelivery: 'env' | 'broker' | undefined;
  toolTiers: Record<string, HitlTier> | undefined;
} {
  return {
    ...integration,
    remoteMcp: integration.remote_mcp ?? undefined,
    stdioMcp: integration.stdio_mcp === true ? true : undefined,
    // ENG-10614: same seam as remoteMcp above. An EMPTY object maps to
    // undefined deliberately - the proxy reads an empty table as "authoritative,
    // every tool unknown" and refuses every call, so a definition that happens
    // to have zero rows must stay ungated rather than be bricked.
    toolTiers: normalizeToolTiers(integration.tool_tiers),
    // ENG-8344: same seam, same reason. Only the exact string 'broker' opts a
    // row into use-time credential delivery — anything else (including a value
    // a newer API might add later) reads as today's env delivery, so an
    // unrecognised value can never strip an agent's `git`/`gh` credentials.
    credentialDelivery: normalizeCredentialDelivery(integration.credential_delivery),
  };
}

/**
 * ENG-10614: keep only well-formed `tool -> tier` pairs, and collapse an empty
 * result to undefined.
 *
 * An unrecognised tier string is DROPPED rather than passed through. The proxy
 * treats anything it does not recognise as non-auto and refuses it, so a typo
 * or a tier a newer API adds would silently gate a tool nobody meant to gate.
 * Dropping it leaves that tool unknown, which the proxy also refuses - the same
 * safe direction, but reached by a rule that is written down rather than by an
 * accident of string comparison.
 */
export function normalizeToolTiers(
  raw: Record<string, string> | null | undefined,
): Record<string, HitlTier> | undefined {
  if (!raw) return undefined;
  const out: Record<string, HitlTier> = {};
  for (const [tool, tier] of Object.entries(raw)) {
    if (typeof tool !== 'string' || !tool) continue;
    if (typeof tier !== 'string') continue;
    if (!(HITL_TIER_ORDER as readonly string[]).includes(tier)) continue;
    out[tool] = tier as HitlTier;
  }
  // ENG-10614 / CodeRabbit: key the absent-vs-present decision on `raw`, NOT on
  // `out`. If the catalog HAD rows and this binary understood none of them
  // (e.g. the platform ships a `write_catastrophic` tier an older agent has
  // never heard of), `out` is empty while the source table is emphatically not.
  // Returning `undefined` there would report "no catalog" and forward every
  // call UNGATED - the exact inversion of the safe direction, and worst on a
  // tier that was added precisely because it is severe.
  //
  // My own commit message argued that dropping an unrecognised tier "leaves the
  // tool unknown, which the proxy also refuses". That is true PER TOOL while
  // some valid entry survives; it is false when every entry is dropped, because
  // the whole table then collapses to absent. Keying on `raw` keeps the
  // distinction the tri-state depends on.
  //
  // ENG-10614 / Obi-Wan `b4947fe5fb8c`: AN EMPTY `raw` IS PRESENT, NOT ABSENT.
  // This line used to `return undefined` here, which silently undid the API's
  // matching fix. `readRemoteMcpToolTiers` claims a toolkit on the first row
  // that names it, precisely so a toolkit with rows-but-none-usable arrives as
  // `{}` rather than being omitted - "authoritative, and nothing is known", so
  // the proxy refuses everything. Collapsing that back to `undefined` one hop
  // later made `hasGatedTool` false, rendered no proxy, and ran the integration
  // UNGATED: the exact outcome the API-side change was written to prevent.
  //
  // The tri-state only survives if BOTH normalisers agree on it:
  //   undefined/null -> absent   (no catalog; forward as before)
  //   {}             -> present, nothing known (refuse every call)
  //   {a: 'read'}    -> present, nothing gated (forward)
  //   {a: 'admin'}   -> present, gated         (refuse a)
  // The field being absent from the payload is still `undefined` via the
  // `if (!raw)` guard at the top, which is the only thing that should mean
  // "no catalog".
  return out;
}

/**
 * ENG-8800 AC5: the normalisation above, with a voice.
 *
 * Behaviour is byte-for-byte what it was — only `'broker'` opts in, everything
 * else is env delivery. The only change is that an UNRECOGNISED value now says
 * so on stderr before degrading.
 *
 * stderr rather than a thrown error on purpose: this runs inside the manager's
 * poll over every integration on the host, and an anomalous marker on one row
 * must not abort the mapping for the rest. Loud, not fatal.
 */
function normalizeCredentialDelivery(value: string | null | undefined): 'broker' | undefined {
  const anomaly = describeUnrecognisedCredentialDelivery(value);
  if (anomaly) {
    process.stderr.write(`[host-integrations] [credential-delivery] ${anomaly}\n`);
  }
  return value === 'broker' ? 'broker' : undefined;
}
